import { twMerge } from "tailwind-merge";
import { HiOutlineOfficeBuilding, HiRefresh } from "react-icons/hi";
import { FaUserPlus } from "react-icons/fa";
import SearchComponent from '../../components/CoreComponents/Search';
import Pagination from '../../components/Pagination/Pagination.tsx';
import Loader from '../../components/CoreComponents/Loader';
import useUserManagement from '../../Helpers/CustomHooks/useUserManagement.ts';

export default function UserManagementPage() {
    const {
        isAdminUser,
        canAddUser,
        isInitialLoading,
        isRefreshing,
        fetchError,
        dismissError,
        searchTerm,
        handleSearch,
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        currentRows,
        currentPage,
        totalPages,
        handleNextPage,
        handlePreviousPage,
        handleRefresh,
        navigateToCreateUser,
        navigateToEditUser,
    } = useUserManagement();

    if (isInitialLoading) {
        return <Loader loaders={{ isUsersLoading: true }} />;
    }

    return (
        <div className="overflow-x-auto">
            {/* Error banner */}
            {fetchError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-800 rounded flex items-center justify-between">
                    <span>{fetchError}</span>
                    <button
                        onClick={dismissError}
                        className="ml-4 text-red-600 hover:text-red-800 font-bold"
                        aria-label="Dismiss error"
                    >
                        &times;
                    </button>
                </div>
            )}

            <div className="flex justify-between items-center mb-4">
                {/* Search Component */}
                <div className="flex-grow mr-4">
                    <SearchComponent onSearch={handleSearch} />
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={handleRefresh}
                        disabled={isRefreshing || !navigator.onLine}
                        className="flex items-center gap-2 px-3 py-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={!navigator.onLine ? 'You are offline' : 'Refresh users'}
                    >
                        <HiRefresh size={20} className={isRefreshing ? 'animate-spin' : ''} />
                        {isRefreshing ? 'Refreshing…' : 'Refresh'}
                    </button>
                    {canAddUser && (
                        <button
                            type="button"
                            className="bg-[#fdb419] float-right"
                            title="Add New User"
                            onClick={navigateToCreateUser}
                        >
                            <span className="flex items-center text-sm font-medium text-white">
                                <FaUserPlus size={18} />
                                <span className="ml-2">Add User</span>
                            </span>
                        </button>
                    )}
                </div>
            </div>

            <table {...getTableProps()} className="min-w-full text-left text-sm">
                <thead className="border-b font-medium dark:border-neutral-500 uppercase">
                    {headerGroups.map(headerGroup => {
                        const { key, ...restHeaderGroupProps } = headerGroup.getHeaderGroupProps();
                        return (
                            <tr key={key} {...restHeaderGroupProps}>
                                {headerGroup.headers.map(column => {
                                    const { key, ...restColumnProps } = column.getHeaderProps();
                                    return (
                                        <th className="px-3 py-2 text-left" key={key} {...restColumnProps}>
                                            {column.render("Header")}
                                        </th>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {currentRows.length === 0 ? (
                        <tr>
                            <td
                                colSpan={headerGroups[0]?.headers.length ?? 5}
                                className="px-3 py-8 text-center text-gray-500"
                            >
                                {searchTerm
                                    ? `No users found matching "${searchTerm}".`
                                    : 'No users available.'}
                            </td>
                        </tr>
                    ) : (
                        currentRows.map((row, i) => {
                            prepareRow(row);
                            const { key, ...restRowProps } = row.getRowProps();
                            return (
                                <tr
                                    key={key}
                                    onClick={isAdminUser ? () => navigateToEditUser(row.original.assignmentUuid) : undefined}
                                    className={twMerge(
                                        isAdminUser ? "cursor-pointer hover:bg-orange-1000 hover:text-white group" : "",
                                        i % 2 === 0
                                            ? "border-b bg-white dark:border-neutral-500 dark:bg-neutral-600"
                                            : "border-b bg-neutral-100 dark:border-neutral-500 dark:bg-neutral-700"
                                    )}
                                    {...restRowProps}
                                >
                                    {row.cells.map(cell => {
                                        const { key, ...restCellProps } = cell.getCellProps();
                                        const isProjectColumn = cell.column.id === 'project';
                                        return (
                                            <td className="px-3 py-2 text-left" key={key} {...restCellProps}>
                                                <div className="flex items-center">
                                                    {isProjectColumn && !!row.values.project && (
                                                        <HiOutlineOfficeBuilding className="mr-2 text-[#fdb419] group-hover:text-white" />
                                                    )}
                                                    {cell.render("Cell")}
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>

            {currentRows.length > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    handleNextPage={handleNextPage}
                    handlePreviousPage={handlePreviousPage}
                />
            )}
        </div>
    );
}